/* Copyright (c) 2022 MOCA Systems, Inc. All rights reserved. */

import { AbstractControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, pluck, takeUntil, startWith, map } from 'rxjs/operators';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';

export enum DisambiguationEventTypes {
	DataChange,
	UserChange,
}

//clean up some of the sktetchiness of managing user input vs data vs either event streams

/**
 * Primarily intended to be used in a ControlValueAccessor situation where we need to be particularly strict about
 * whether or not any given event is driven by a user change or a system/ data level change.
 *
 * Operates by hooking into an angular form, providing a centralized "patchValue" method and providing streams to listen to and react.
 * To use this, you _must_ run everything through this class. This includes
 * - writeValue
 * - every "internalForm" setter
 * - and any other derived and/ or calculated updates
 * After which you can use an instance of this class to listen to and react to user events, system events and both.
 *
 * As an added bonus, the inputs and outputs of this class are strongly typed, unlike form valueChanges
 *
 * NOTE: this class does not maintain state of the streams. If you need re-emission consider resubscription one of the outputs to your own BehaviorSubject
 */
export class UserSystemDisambiguation<T extends object> {
	private logger: ILoggerService;
	private model: T;

	private shutdownSubject$ = new Subject<void>();

	//emits only after data is settled
	private _coreData = new Subject<{ emissionType: DisambiguationEventTypes; data: T }>();

	/** Emits on anything that is determined to be a direct user change */
	public userChange$ = this._coreData.pipe(
		filter((emission) => emission.emissionType === DisambiguationEventTypes.UserChange),
		pluck('data')
	);

	/** Emits on "non-user" changes */
	public systemChange$ = this._coreData.pipe(
		filter((emission) => emission.emissionType === DisambiguationEventTypes.DataChange),
		pluck('data')
	);

	/** Observable that emits regardless of whether or not it's a data change or a user change */
	public dataChange$ = this._coreData.pipe(pluck('data'));

	public dataChangeRaw$ = this._coreData.asObservable();

	/** apply an update to the "internal model" */
	public patchValue(value: Partial<T>, type: DisambiguationEventTypes = DisambiguationEventTypes.DataChange) {
		const patchedModel = { ...(this.model || {}), ...value } as T;

		// infinite loop quite possible, manually apply user events at your own risk
		if (type === DisambiguationEventTypes.UserChange) {
			this.logger.error('NYI'); //as currently setup this will cause duplicate emissions, preferred fix... "unknown"
			// this.form.patchValue(value);
		} else if (type === DisambiguationEventTypes.DataChange) {
			this.form.patchValue(value, { emitEvent: false });
		}

		this._coreData.next({ emissionType: type, data: patchedModel });
	}

	/** a hook that can be extended to transform form output to T */
	protected transformFormValue(value: any): T {
		return value;
	}

	constructor(private form: AbstractControl) {
		this.logger = LoggerServiceFactory.create('UserSystemDisambiguation');
		this._coreData.pipe(takeUntil(this.shutdownSubject$)).subscribe((val) => {
			this.model = val.data;
		});

		form.valueChanges
			.pipe(
				startWith(form.value),
				map((val) => this.transformFormValue(val)),
				map((data) => ({ emissionType: DisambiguationEventTypes.UserChange, data })),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(this._coreData);
	}

	public destroy() {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}
}
